import React, { useContext } from 'react';
import { DataContext } from 'App';
import { t } from 'i18n';
import { useNavigate, Link } from 'react-router-dom';
import { MdClose, MdNavigateNext, MdAddShoppingCart } from 'react-icons/md';
import { FaFacebookF, FaTwitter, FaInstagram, FaYoutube, FaPinterest } from 'react-icons/fa';
import CartService from '../../services/cart';
import WishlistService from '../../services/wishlist';
import AuthService from '../../services/auth';
import { handleApiError, handleApiSuccess } from 'components/Handle';
import { formatPrice } from 'utils/formatPrice';
import { getPrimaryProductImageSrc } from 'utils/productImages';
import { productPath } from 'utils/storePaths';

const Wishlist = () => {
  const { wishList, setWishList, setCartList, setSnackbar } = useContext(DataContext);
  const navigate = useNavigate();

  const handleClick = (event) => {
    event.preventDefault();
    navigate(event.target.pathname);
  };

  const deleteFromWishlist = async (item) => {
    if (!item) return;

    if (!AuthService.isAuthenticated() || !item.id) {
      const updated = wishList.filter((w) => w.product?.id !== item.product?.id);
      setWishList(updated);
      WishlistService.saveLocalWishlist(updated);
      setSnackbar({ ...handleApiSuccess(t('wishlistMsg.removed')), open: true });
      return;
    }

    try {
      const result = await WishlistService.removeFromWishlist(item.id);
      if (result.success) {
        setWishList((prevState) => prevState.filter((w) => w.product?.id !== item.product?.id));
        setSnackbar({ ...handleApiSuccess(t('wishlistMsg.removed')), open: true });
      } else {
        setSnackbar({ ...handleApiError(result.error), open: true });
      }
    } catch (error) {
      console.error('Error removing wishlist item:', error);
      setSnackbar({ ...handleApiError(t('wishlistMsg.removeFailed')), open: true });
    }
  };

  /** Daraz-style: merge into cart via CartService, then remove wishlist row; toast on success. */
  const moveToCartFromWishlist = async (item) => {
    if (!item) return;

    try {
      const result = await WishlistService.moveToCartFromWishlist(item);
      if (result.success) {
        try {
          const cartResult = await CartService.getCart();
          if (cartResult.success) {
            setCartList(cartResult.cartItems);
          }
        } catch (e) {
          console.error('Refresh cart after move:', e);
        }
        try {
          if (AuthService.isAuthenticated()) {
            const wlResult = await WishlistService.getWishlist();
            if (wlResult.success) {
              setWishList(wlResult.wishlistItems);
            }
          } else {
            setWishList(WishlistService.loadLocalWishlist());
          }
        } catch (e) {
          console.error('Refresh wishlist after move:', e);
        }
        setSnackbar({
          ...handleApiSuccess(t('wishlistMsg.moved')),
          open: true,
        });
      } else {
        setSnackbar({
          ...handleApiError(result.error || t('wishlistMsg.moveFailed')),
          open: true,
        });
      }
    } catch (error) {
      console.error('Error moving to cart:', error);
      setSnackbar({
        ...handleApiError(t('wishlistMsg.moveFailedCatch')),
        open: true,
      });
    }
  };

  return (
    <>
      {wishList.length === 0 ? (
        <div className="flex min-h-[60vh] flex-col items-center justify-center bg-gradient-to-b from-neutral-700/5 to-white px-4">
          <div className="w-full max-w-lg rounded-[28px] border border-amber-100 bg-white px-10 py-12 text-center shadow-xl">
            <h2 className="mb-3.5 text-3xl font-normal text-neutral-800">{t('wishlist.emptyTitle')}</h2>
            <p className="mb-6 text-base text-neutral-600">{t('wishlist.emptyBody')}</p>
            <button
              type="button"
              onClick={() => navigate('/shop')}
              className="rounded-full border-none bg-primary px-7 py-3.5 text-sm font-bold text-on-accent shadow transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              {t('wishlist.start')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            className="theme-light flex flex-col items-center justify-center bg-neutral-200 bg-cover bg-center bg-no-repeat py-11"
            style={{ backgroundImage: "url('/images/banners/banner-tech2.webp')" }}
          >
            <h5 className="text-[40px] font-normal text-neutral-800">{t('nav.wishlist')}</h5>
            <h6 className="text-xl font-normal text-primary">{t('nav.shop')}</h6>
          </div>
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <nav className="px-2.5 py-3.5 pb-10" aria-label={t('common.breadcrumb')}>
              <ol className="flex flex-wrap items-center gap-1 text-sm" role="presentation" onClick={handleClick}>
                <li>
                  <a
                    href="/"
                    className="text-neutral-700 no-underline transition-colors duration-200 hover:text-primary"
                  >
                    {t('nav.home')}
                  </a>
                </li>
                <li className="flex items-center gap-0.5 text-neutral-400">
                  <MdNavigateNext className="text-base rtl:-scale-x-100" aria-hidden />
                  <a
                    href="/wishlist"
                    aria-current="page"
                    className="font-medium text-neutral-900 no-underline"
                  >
                    {t('nav.wishlist')}
                  </a>
                </li>
              </ol>
            </nav>
            <div>
              <table className="w-full border-collapse text-neutral-900 max-md:[&_thead]:hidden">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="py-3.5 text-start text-sm font-normal text-neutral-500">{t('wishlist.product')}</th>
                    <th className="py-3.5 text-start text-sm font-normal text-neutral-500">{t('wishlist.price')}</th>
                    <th className="py-3.5" />
                    <th className="py-3.5" />
                  </tr>
                </thead>
                <tbody>
                  {wishList.map((item) => {
                    const wishlistProduct = item.product || item;
                    if (!wishlistProduct) return null;

                    return (
                      <tr
                        key={wishlistProduct.id || item.id}
                        className="relative border-b border-neutral-200 max-md:mb-4 max-md:flex max-md:flex-col max-md:border max-md:border-neutral-300 max-md:py-10 max-md:px-2"
                      >
                        <td className="max-md:w-full">
                          <div className="flex max-md:flex-col max-md:items-center">
                            <div className="me-7 block h-20 w-20 shrink-0 max-md:mx-auto max-md:mb-3">
                              <img
                                src={getPrimaryProductImageSrc(wishlistProduct)}
                                alt={t('product.productLower')}
                                referrerPolicy="no-referrer"
                                className="h-full w-full object-contain"
                              />
                            </div>
                            <Link
                              to={productPath(wishlistProduct)}
                              className="inline-block max-w-[300px] truncate text-base font-normal text-neutral-800 no-underline max-md:max-w-[60%] max-md:whitespace-normal max-md:text-center"
                            >
                              {wishlistProduct.name || wishlistProduct.title}
                            </Link>
                          </div>
                        </td>
                        <td>
                          <h4 className="w-[180px] text-base font-normal max-md:mx-auto max-md:my-1 max-md:text-center">
                            {formatPrice(wishlistProduct.price)}
                          </h4>
                        </td>
                        <td className="max-md:mt-4">
                          <button
                            type="button"
                            onClick={() => moveToCartFromWishlist(item)}
                            className="flex h-10 w-[202px] max-w-full items-center justify-center border border-primary bg-transparent px-4 text-[0.813rem] text-[#d0a171] transition-colors hover:bg-[#d0a171] hover:text-white"
                          >
                            <MdAddShoppingCart className="me-4 text-base shrink-0" aria-hidden />
                            {t('wishlist.moveToCart')}
                          </button>
                        </td>
                        <td>
                          <div
                            role="button"
                            tabIndex={0}
                            className="flex w-10 cursor-pointer items-center justify-end text-neutral-300 max-md:absolute max-md:end-6 max-md:top-2 max-md:w-auto max-md:justify-center max-md:p-2 hover:text-neutral-800"
                            onClick={() => deleteFromWishlist(item)}
                            onKeyDown={(e) => e.key === 'Enter' && deleteFromWishlist(item)}
                          >
                            <MdClose className="text-xl transition-colors" aria-hidden />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="my-8 mb-5 flex items-center">
              <p className="text-sm font-light text-neutral-600">{t('wishlist.share')}</p>
              <div className="ms-4 flex">
                {[FaFacebookF, FaTwitter, FaInstagram, FaYoutube, FaPinterest].map((Icon, i) => (
                  <div
                    key={i}
                    className="ms-4 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-neutral-200 text-xs text-neutral-500 transition-colors hover:border-primary hover:text-primary"
                  >
                    <Icon />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Wishlist;
